import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout, Select, Spin, Tabs, message } from 'antd';
import { WalletOutlined, FolderOutlined, InfoCircleOutlined } from '@ant-design/icons';
import apiService from '../../services/api';
import { ServiceType } from '../../services/api';
import { usePortfolios, Portfolio } from '../../hooks/usePortfolios';
import type { PortfolioResponseDto, BalanceResponseDto } from '../../api-schema/generated/types';
import {
  HomeContainer,
  PageHeader,
  PageTitle,
  PageSubtitle,
} from '../Home/styles';
import {
  Card as StyledCard,
} from '../PairTrading/styles';
import { formatNumber } from '../../utils/numberFormat';
import BasicHoldings from '../../components/BasicHoldings';
import PerformanceMetrics from '../../components/PerformanceMetrics';

const coreService = apiService.getService(ServiceType.CORE);
const { Content } = Layout;

const PortfolioPage: React.FC = () => {
  const navigate = useNavigate();
  const { portfolios, selectedPortfolioId, setSelectedPortfolioId, selectedPortfolio, loading: portfoliosLoading } = usePortfolios();
  const [portfolioDetail, setPortfolioDetail] = useState<PortfolioResponseDto | null>(null);
  const [balance, setBalance] = useState<BalanceResponseDto | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [tab, setTab] = useState('balance');

  const fetchPortfolioDetail = useCallback(async () => {
    if (!selectedPortfolioId) {
      setPortfolioDetail(null);
      return;
    }
    try {
      setLoadingDetail(true);
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/signin');
        return;
      }
      const response = await coreService.get<PortfolioResponseDto>(`/portfolios/${selectedPortfolioId}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      setPortfolioDetail(response.data);
    } catch (err: any) {
      console.error('Error fetching portfolio detail:', err);
      message.error('Failed to load portfolio details');
      setPortfolioDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, [selectedPortfolioId, navigate]);

  const fetchBalance = useCallback(async () => {
    if (!selectedPortfolioId) {
      setBalance(null);
      return;
    }
    try {
      setLoadingBalance(true);
      const token = localStorage.getItem('access_token');
      if (!token) {
        message.error('Please login first');
        navigate('/signin');
        return;
      }
      const response = await coreService.get<BalanceResponseDto>('/balance', {
        params: { portfolioId: selectedPortfolioId },
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      setBalance(response.data);
    } catch (err: any) {
      console.error('Error fetching balance:', err);
      message.error('Failed to load balance information');
      setBalance(null);
    } finally {
      setLoadingBalance(false);
    }
  }, [selectedPortfolioId, navigate]);

  useEffect(() => {
    fetchPortfolioDetail();
  }, [fetchPortfolioDetail]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  /**
   * The balance cards, lifted out of the return so the page can carry tabs.
   *
   * Unchanged otherwise — this is a move, not a rewrite. What it shows is the BROKER's view
   * of the account; the tab beside it shows the part of that account no strategy has taken
   * responsibility for, which is a different question and had no home before.
   */
  const balanceBody =
    (loadingDetail || loadingBalance) && selectedPortfolioId ? (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {portfolioDetail && (
              <StyledCard
                title={
                  <span>
                    <InfoCircleOutlined style={{ marginRight: '6px', color: '#0ECB81' }} />
                    Portfolio Details
                  </span>
                }
              >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '8px' }}>
                  <div>
                    <div style={{ color: '#848E9C', fontSize: '14px', marginBottom: '2px' }}>Portfolio ID</div>
                    <div style={{ fontSize: '16px', fontWeight: 600 }}>{portfolioDetail.portfolioId}</div>
                  </div>
                  <div>
                    <div style={{ color: '#848E9C', fontSize: '14px', marginBottom: '2px' }}>Name</div>
                    <div style={{ fontSize: '16px', fontWeight: 600 }}>{portfolioDetail.portfolioName ?? '–'}</div>
                  </div>
                  <div>
                    <div style={{ color: '#848E9C', fontSize: '14px', marginBottom: '2px' }}>Broker</div>
                    <div style={{ fontSize: '16px', fontWeight: 600 }}>ALGOTRADE</div>
                  </div>
                  <div>
                    <div style={{ color: '#848E9C', fontSize: '14px', marginBottom: '2px' }}>Status</div>
                    <div style={{ fontSize: '16px', fontWeight: 600 }}>{portfolioDetail.status ?? (portfolioDetail.isActive ? 'Active' : '–')}</div>
                  </div>
                </div>
              </StyledCard>
            )}

            {balance ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: '8px' }}>
            <div
              style={{
                gridColumn: '1 / -1',
                display: 'grid',
                // Was a hard `repeat(3, 1fr)`: on a phone that is three ~110px tracks whose
                // titles do not fit, so the row ran off the right edge. auto-fit lets it fall
                // to one card per row instead.
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
                gap: '8px',
              }}
            >
            <StyledCard
              title={
                <span>
                  <WalletOutlined style={{ marginRight: '6px', color: '#0ECB81' }} />
                  Total Assets & Liabilities
                </span>
              }
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div>
                  <div style={{ color: '#848E9C', fontSize: '14px', marginBottom: '2px' }}>Total Assets</div>
                  <div style={{ fontSize: '24px', fontWeight: 600, color: '#0ECB81' }}>
                    {formatNumber(balance.totalassets)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#848E9C', fontSize: '14px', marginBottom: '2px' }}>Liabilities</div>
                  <div style={{ fontSize: '24px', fontWeight: 600, color: '#F6465D' }}>
                    {formatNumber(balance.liabilities)}
                  </div>
                </div>
              </div>
            </StyledCard>

            <StyledCard
              title={
                <span>
                  <WalletOutlined style={{ marginRight: '6px', color: '#0ECB81' }} />
                  Net Asset Value
                </span>
              }
            >
              <div style={{ fontSize: '24px', fontWeight: 600, color: '#0ECB81' }}>
                {formatNumber(balance.netassetvalue)}
              </div>
            </StyledCard>

            <StyledCard
              title={
                <span>
                  <WalletOutlined style={{ marginRight: '6px', color: '#0ECB81' }} />
                  Purchasing Power
                </span>
              }
            >
              <div style={{ fontSize: '24px', fontWeight: 600, color: '#0ECB81' }}>
                {formatNumber(balance.purchasingpower)}
              </div>
            </StyledCard>
            </div>

            {balance.currentbalance && (
            <StyledCard
              title="Current Balance Details"
              style={{ gridColumn: '1 / -1' }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '8px' }}>
                <div>
                  <div style={{ color: '#848E9C', fontSize: '14px', marginBottom: '4px' }}>Available Balance</div>
                  <div style={{ fontSize: '18px', fontWeight: 600, color: '#0ECB81' }}>
                    {formatNumber(balance.currentbalance.availablebalance ?? 0)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#848E9C', fontSize: '14px', marginBottom: '4px' }}>Net Receivable Amount</div>
                  <div style={{ fontSize: '18px', fontWeight: 600 }}>
                    {formatNumber(balance.currentbalance.netreceivableamount ?? 0)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#848E9C', fontSize: '14px', marginBottom: '4px' }}>Secure Amount</div>
                  <div style={{ fontSize: '18px', fontWeight: 600 }}>
                    {formatNumber(balance.currentbalance.secureamount ?? 0)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#848E9C', fontSize: '14px', marginBottom: '4px' }}>Cash Dividend</div>
                  <div style={{ fontSize: '18px', fontWeight: 600 }}>
                    {formatNumber(balance.currentbalance.cashdividend ?? 0)}
                  </div>
                </div>
              </div>
            </StyledCard>
            )}
          </div>
            ) : selectedPortfolioId ? (
              <div style={{ textAlign: 'center', padding: '24px', color: '#848E9C' }}>
                No balance data available for this portfolio
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '24px', color: '#848E9C' }}>
                Please select a portfolio to view details and balance
              </div>
            )}
          </div>
        );

  return (
    <HomeContainer>
      <Content style={{ padding: '16px' }}>
            <PageHeader>
              <div>
                <PageTitle>Portfolio</PageTitle>
                <PageSubtitle>Your balance, and what of it no strategy is trading</PageSubtitle>
              </div>
            </PageHeader>

            <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <label style={{ display: 'block', marginBottom: '4px', color: '#848E9C', fontSize: '14px', fontWeight: 600 }}>
            <FolderOutlined style={{ marginRight: '6px' }} />
            Select Portfolio
          </label>
          <Select
            style={{ width: '100%' }}
            size="large"
            value={selectedPortfolioId}
            onChange={setSelectedPortfolioId}
            loading={portfoliosLoading}
            placeholder="Select a portfolio"
          >
            {portfolios.map((portfolio: Portfolio) => (
              <Select.Option key={portfolio.id} value={portfolio.id}>
                {portfolio.portfolioName || portfolio.portfolioId}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>

      <Tabs
        size="small"
        items={[
          { key: 'balance', label: 'Balance', children: balanceBody },
          {
            // Account-wide: grid and pair on this portfolio both count in these numbers.
            key: 'performance',
            label: 'Performance',
            children: (
              <PerformanceMetrics
                portfolioId={selectedPortfolioId}
                active={tab === 'performance'}
              />
            ),
          },
          {
            // "What is not being traded" — shares no strategy claims, and the cash none has
            // been funded with. The grid page has the same view with Import; here it is
            // read-only, since importing is a grid action.
            key: 'basic',
            label: 'Not trading',
            children: (
              <BasicHoldings
                portfolioId={selectedPortfolioId}
                active={tab === 'basic'}
              />
            ),
          },
        ]}
        activeKey={tab}
        onChange={setTab}
      />

      </Content>
    </HomeContainer>
  );
};

export default PortfolioPage;

